/**
 * SeasonalityLocationSummary
 *
 * The "Location Summary - Monthly" table at the top of the Monthly Seasonality view (total
 * gross / expenses / operating profit by month, comparison-mode actual columns and the
 * location-level comparison chart). Extracted verbatim from SeasonalityModeView.
 */

import React from 'react';
import { MonthlyTotals, MONTH_NAMES, addMonthlyTotals } from '../../types';
import { applySeasonality } from '../../utils/seasonalityCalculation';
import { MonthlyDataRow } from './SeasonalityRowComponents';
import { ProjectionComparisonChart } from './ProjectionComparisonChart';
import { HistoricalYearBundle, SeasonalityModeViewProps } from './seasonalityModeTypes';
import { SeasonalityModeData } from './useSeasonalityModeData';

export interface LocationMonthlyTotals {
  totalGross: number;
  totalExpenses: number;
  operatingProfit: number;
  monthlyGross: MonthlyTotals;
  monthlyExpenses: MonthlyTotals;
  monthlyProfit: MonthlyTotals;
}

interface SeasonalityLocationSummaryProps extends Pick<SeasonalityModeViewProps, 'targetYear' | 'formatCurrency' | 'getTotalProjectedUnits'> {
  comparisonMode: boolean;
  actualData?: HistoricalYearBundle | null;
  data: SeasonalityModeData;
  locationTotals: LocationMonthlyTotals;
  locationActuals: LocationMonthlyTotals | null;
  visibleActualMonths: Set<number>;
  toggleActualMonth: (month: number) => void;
  expandedCharts: Set<string>;
  toggleChart: (sectionKey: string) => void;
}

export const SeasonalityLocationSummary: React.FC<SeasonalityLocationSummaryProps> = ({
  targetYear,
  formatCurrency,
  getTotalProjectedUnits,
  comparisonMode,
  actualData,
  data,
  locationTotals,
  locationActuals,
  visibleActualMonths,
  toggleActualMonth,
  expandedCharts,
  toggleChart,
}) => {
  const { getDeptUnitSeasonalityWeights, actualUnitCounts } = data;

  // Render a row with monthly data - delegates to MonthlyDataRow component
  const renderMonthlyRow = (
    label: string,
    monthly: MonthlyTotals,
    annualTotal: number,
    colorClass: string,
    isBold: boolean = false,
    indent: number = 0,
    actualMonthly?: MonthlyTotals | null,
    actualAnnual?: number | null,
    isExpense: boolean = false
  ) => (
    <MonthlyDataRow
      label={label}
      monthly={monthly}
      annualTotal={annualTotal}
      colorClass={colorClass}
      isBold={isBold}
      indent={indent}
      actualMonthly={actualMonthly}
      actualAnnual={actualAnnual}
      isExpense={isExpense}
      comparisonMode={comparisonMode}
      visibleActualMonths={visibleActualMonths}
      formatCurrency={formatCurrency}
    />
  );

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden print-department-section">
      <div className="bg-gray-700 text-white px-4 py-2 flex justify-between items-center print-department-header">
        <h3 className="font-semibold">Location Summary - Monthly {targetYear}</h3>
      {comparisonMode && (
        <button
          onClick={() => toggleChart('LOCATION_SUMMARY')}
          className={`p-1.5 rounded hover:bg-gray-600 transition-colors no-print ${expandedCharts.has('LOCATION_SUMMARY') ? 'bg-blue-500' : ''}`}
          title={expandedCharts.has('LOCATION_SUMMARY') ? 'Hide comparison chart' : 'Show comparison chart'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16" />
          </svg>
        </button>
      )}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100 text-xs">
            <th className="min-w-[180px] px-3 py-2 text-left font-semibold sticky left-0 bg-gray-100">Metric</th>
            {MONTH_NAMES.map((month, i) => {
              const monthNum = i + 1;
              const showActual = comparisonMode && visibleActualMonths.has(monthNum);
              const hasActualForMonth = actualData !== null && actualData !== undefined;

              return (
                <React.Fragment key={month}>
                  <th
                    className={`min-w-[75px] px-1 py-2 text-right font-semibold ${comparisonMode && hasActualForMonth ? 'cursor-pointer hover:bg-gray-200' : ''}`}
                    onClick={() => comparisonMode && hasActualForMonth && toggleActualMonth(monthNum)}
                    title={comparisonMode && hasActualForMonth ? `Click to ${showActual ? 'hide' : 'show'} actuals` : ''}
                  >
                    <span className="flex items-center justify-end gap-1">
                      {month}
                      {comparisonMode && hasActualForMonth && (
                        <span className={`text-[9px] ${showActual ? 'text-blue-500' : 'text-gray-400'}`}>
                          {showActual ? '▼' : '▶'}
                        </span>
                      )}
                    </span>
                    {showActual && <span className="block text-xs text-gray-500 font-normal">Proj</span>}
                  </th>
                  {showActual && (
                    <th className="min-w-[75px] px-1 py-2 text-right font-semibold bg-blue-100">
                      <span className="block text-xs text-blue-600 font-normal">Act</span>
                    </th>
                  )}
                </React.Fragment>
              );
            })}
            <th className="min-w-[100px] px-2 py-2 text-right font-bold bg-blue-100">{targetYear} Proj</th>
            {comparisonMode && visibleActualMonths.size > 0 && (
              <th className="min-w-[100px] px-2 py-2 text-right font-bold bg-blue-200">{targetYear} Act</th>
            )}
          </tr>
        </thead>
        <tbody>
          {renderMonthlyRow(
            'Total Gross',
            locationTotals.monthlyGross,
            locationTotals.totalGross,
            'bg-green-50 text-green-900',
            true,
            0,
            locationActuals?.monthlyGross,
            locationActuals?.totalGross,
            false
          )}
          {renderMonthlyRow(
            'Total Expenses',
            locationTotals.monthlyExpenses,
            locationTotals.totalExpenses,
            'bg-orange-50 text-orange-900',
            true,
            0,
            locationActuals?.monthlyExpenses,
            locationActuals?.totalExpenses,
            true
          )}
          {renderMonthlyRow(
            'Operating Profit',
            locationTotals.monthlyProfit,
            locationTotals.operatingProfit,
            locationTotals.operatingProfit >= 0 ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-900',
            true,
            0,
            locationActuals?.monthlyProfit,
            locationActuals?.operatingProfit,
            false
          )}
        </tbody>
      </table>
    </div>
    {/* Location Summary Comparison Chart */}
    {comparisonMode && expandedCharts.has('LOCATION_SUMMARY') && (() => {
      // Calculate location-level projected units (New + Used)
      const projectedNewUnits = getTotalProjectedUnits('NEW_VEHICLE');
      const projectedUsedUnits = getTotalProjectedUnits('USED_VEHICLE');

      // Get monthly distribution for units
      const newUnitWeights = getDeptUnitSeasonalityWeights('NEW_VEHICLE');
      const usedUnitWeights = getDeptUnitSeasonalityWeights('USED_VEHICLE');
      const monthlyNewUnits = applySeasonality(projectedNewUnits, newUnitWeights);
      const monthlyUsedUnits = applySeasonality(projectedUsedUnits, usedUnitWeights);
      const projectedUnitsMonthly = addMonthlyTotals(monthlyNewUnits, monthlyUsedUnits);

      // Actual units from historical data
      const actualNewUnitsMonthly = actualUnitCounts?.monthlyNewUnits;
      const actualUsedUnitsMonthly = actualUnitCounts?.monthlyUsedUnits;
      const actualUnitsMonthly = actualNewUnitsMonthly && actualUsedUnitsMonthly
        ? addMonthlyTotals(actualNewUnitsMonthly, actualUsedUnitsMonthly)
        : null;

      return (
        <ProjectionComparisonChart
          title="Projected vs Actual by Month"
          projectedGross={locationTotals.monthlyGross}
          actualGross={locationActuals?.monthlyGross || null}
          projectedExpenses={locationTotals.monthlyExpenses}
          actualExpenses={locationActuals?.monthlyExpenses || null}
          projectedProfit={locationTotals.monthlyProfit}
          actualProfit={locationActuals?.monthlyProfit || null}
          projectedUnits={projectedUnitsMonthly}
          actualUnits={actualUnitsMonthly}
          showUnits={true}
        />
      );
    })()}
    </div>
  );
};
